import { useSelector } from 'react-redux';
import ArgumentInput from './ArgumentInput';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import argumentSelectors from '@/redux/selectors/argumentSelectors.js';
import { useMemo, useRef } from 'react';
import { randomId } from '@shared/utils/randomId.js';

const ArgumentMultipleValuesWrapper = ({ disabled, definition, argument, dataSource, dataSourcePath, dataSavePath }) => {
  const values = useSelector(memoizeSelector(`argumentValue-${argument?.key}`, (state) => argumentSelectors[dataSource].activeArguments()(state)?.[argument?.key]));
  const emptyItemIdRef = useRef(randomId());
  const previousLengthRef = useRef(0);
  const itemIdsMapRef = useRef(new Map());

  const initialValues = useMemo(() => {
    const safeValues = Array.isArray(values) ? values : [];
    const currentLength = safeValues.length;

    if (currentLength > previousLengthRef.current) {
      emptyItemIdRef.current = randomId();
    }

    if (currentLength < previousLengthRef.current) {
      const newMap = new Map();
      safeValues.forEach((val, idx) => {
        if (val?.id) {
          newMap.set(idx, val.id);
        } else if (itemIdsMapRef.current.has(idx)) {
          newMap.set(idx, itemIdsMapRef.current.get(idx));
        }
      });
      itemIdsMapRef.current = newMap;
    }

    previousLengthRef.current = currentLength;

    const valuesWithStableIds = safeValues.map((val, idx) => {
      const existingStableId = itemIdsMapRef.current.get(idx);

      if (val?.id && existingStableId && val.id === existingStableId) {
        return val;
      }

      if (val?.id && !existingStableId) {
        itemIdsMapRef.current.set(idx, val.id);
        return val;
      }

      if (!existingStableId) {
        itemIdsMapRef.current.set(idx, randomId());
      }

      const stableId = itemIdsMapRef.current.get(idx);

      if (typeof val === 'object' && val !== null) {
        return { ...val, id: stableId };
      }

      return { value: val, id: stableId };
    });

    return [...valuesWithStableIds, { id: emptyItemIdRef.current }];
  }, [values]);

  return (
    <div className={'multiple-values-wrapper'}>
      {initialValues.map((val, idx) => (
        <ArgumentInput //
          disabled={disabled}
          definition={definition}
          argument={argument}
          dataSource={dataSource}
          dataSourcePath={dataSourcePath}
          dataSavePath={dataSavePath}
          key={val?.id || idx}
          index={idx}
          id={val?.id}
        />
      ))}
    </div>
  );
};

export default ArgumentMultipleValuesWrapper;
